import React, { useState } from 'react'
import { useSubscription } from '../hooks/useSubscription'
import { X, RefreshCw, AlertTriangle, CheckCircle, XCircle } from 'lucide-react'
import { API_CONFIG } from '../constants/apiConfig'

const SubscriptionDebugModal = ({ isOpen, onClose }) => {
  const {
    subscription,
    hasActiveSubscription,
    creditsRemaining,
    subscriptionExpiry,
    packageName,
    loading,
    error,
    refreshData
  } = useSubscription()
  
  const [debugResults, setDebugResults] = useState({})
  const [isRefreshing, setIsRefreshing] = useState(false)

  if (!isOpen) return null

  const handleRefresh = async () => {
    setIsRefreshing(true)
    try {
      await refreshData()
      // Simple test: check if subscription is active
      const testActions = ['web_search', 'academic_search', 'gambar_agent', 'artikel_agent', 'ppt_agent']
      const results = {}
      
      for (const action of testActions) {
        results[action] = { 
          canPerform: hasActiveSubscription, 
          detailed: { canPerform: hasActiveSubscription, reason: hasActiveSubscription ? 'Active subscription' : 'No active subscription' }
        }
      }
      
      setDebugResults(results)
    } catch (err) {
      console.error('Error refreshing subscription data:', err)
    } finally {
      setIsRefreshing(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('id-ID')
  }

  const StatusIcon = ({ status }) => {
    if (status === true) return <CheckCircle className="w-4 h-4 text-green-500" />
    if (status === false) return <XCircle className="w-4 h-4 text-red-500" />
    return <AlertTriangle className="w-4 h-4 text-yellow-500" />
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700">
          <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 text-yellow-400" />
            <span>Subscription Debug Information</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Refresh Button */}
          <div className="flex justify-between items-center">
            <p className="text-gray-300 text-sm">
              Debug mode untuk mendiagnosis masalah subscription
            </p>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>{isRefreshing ? 'Refreshing...' : 'Refresh Data'}</span>
            </button>
          </div>

          {/* Basic Subscription Info */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Loading:</span>
                  <StatusIcon status={!loading} />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Has Active Subscription:</span>
                  <StatusIcon status={hasActiveSubscription} />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Credits Remaining:</span>
                  <span className="text-white">{creditsRemaining || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Package Name:</span>
                  <span className="text-white">{packageName || 'N/A'}</span>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Subscription ID:</span>
                  <span className="text-white text-xs">{subscription?.id || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className="text-white">{subscription?.status || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Expires At:</span>
                  <span className="text-white text-xs">{formatDate(subscriptionExpiry)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Error:</span>
                  <span className="text-red-400 text-xs">{error?.message || 'None'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Raw Subscription Object */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">Raw Subscription Data</h3>
            <pre className="text-xs text-gray-300 bg-black rounded p-3 overflow-x-auto">
              {JSON.stringify(subscription, null, 2)}
            </pre>
          </div>

          {/* Action Test Results */}
          {Object.keys(debugResults).length > 0 && (
            <div className="bg-gray-900 rounded-lg p-4">
              <h3 className="text-lg font-medium text-white mb-4">Action Test Results</h3>
              <div className="space-y-3">
                {Object.entries(debugResults).map(([action, result]) => (
                  <div key={action} className="border border-gray-700 rounded p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-white font-medium">{action}</span>
                      <StatusIcon status={result.canPerform} />
                    </div>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span className="text-gray-400">Can Perform:</span>
                        <span className={result.canPerform ? 'text-green-400' : 'text-red-400'}>
                          {result.canPerform ? 'Yes' : 'No'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Detailed Result:</span>
                        <span className={result.detailed?.canPerform ? 'text-green-400' : 'text-red-400'}>
                          {result.detailed?.canPerform ? 'Yes' : 'No'}
                        </span>
                      </div>
                      {result.detailed?.reason && (
                        <div className="flex justify-between">
                          <span className="text-gray-400">Reason:</span>
                          <span className="text-yellow-400 text-xs">{result.detailed.reason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Troubleshooting Steps */}
          <div className="bg-gray-900 rounded-lg p-4">
            <h3 className="text-lg font-medium text-white mb-4">Troubleshooting Steps</h3>
            <div className="space-y-2 text-sm text-gray-300">
              <div className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">1.</span>
                <span>Pastikan user sudah login dan memiliki subscription aktif</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">2.</span>
                <span>Periksa apakah subscription.status === 'active'</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">3.</span>
                <span>Pastikan subscription belum expired (expires_at &gt; now)</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">4.</span>
                <span>Periksa apakah credits_remaining mencukupi untuk action yang diminta</span>
              </div>
              <div className="flex items-start space-x-2">
                <span className="text-blue-400 font-bold">5.</span>
                <span>Untuk agent actions, pastikan user memiliki akses ke agent tersebut</span>
              </div>
            </div>
          </div>

          {/* Debug Mode Notice */}
          {API_CONFIG.DEBUG && (
            <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-yellow-400">
                <AlertTriangle className="w-4 h-4" />
                <span className="font-medium">Debug Mode Active</span>
              </div>
              <p className="text-yellow-300 text-sm mt-1">
                Debug mode is enabled. Check console for detailed logs.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-700 p-6">
          <div className="flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SubscriptionDebugModal